import { motion } from 'motion/react';

import { TextAnimate } from '@shared/ui/text-animate';
import { Heading } from '@shared/ui/typography';

interface LandingHeroTextProps {
  description: string;
  headingId: string;
  firstLine: string;
  secondLine: string;
}

export const LandingHeroText = ({
  description,
  headingId,
  firstLine,
  secondLine,
}: LandingHeroTextProps) => {
  const heroEase = [0.4, 1.2, 0.5, 1] as const;

  const animationSpeed = 2;

  const firstLineDelay = 0.05;
  const firstLineDuration = animationSpeed / 4;
  const secondLineDelay = animationSpeed / 3;
  const secondLineDuration = animationSpeed / 2;
  const firstLineEnterTransition = {
    duration: animationSpeed / 2,
    ease: heroEase,
  };
  const descriptionTransition = {
    duration: animationSpeed,
    delay: secondLineDelay + secondLineDuration - animationSpeed / 5,
    ease: heroEase,
  };

  const revealTransition = (t: number) => {
    return {
      duration: t,
      delay: secondLineDelay,
      ease: heroEase,
    };
  };

  return (
    <div
      className="
        flex flex-col gap-3
        max-sm:items-center
        sm:gap-4
        md:gap-6
      "
    >
      <Heading
        tag="h1"
        id={headingId}
        size="max"
        className="
          relative font-[450]
          max-sm:text-center
          sm:font-normal
        "
      >
        <TextAnimate
          as="span"
          by="word"
          animation="slideRight"
          startOnView={false}
          once
          delay={firstLineDelay}
          duration={firstLineDuration}
          enterTransition={firstLineEnterTransition}
          className="block whitespace-normal text-foreground"
          segmentClassName="will-change-transform"
        >
          {firstLine}
        </TextAnimate>
        <span
          className="
            relative block w-fit overflow-hidden
            max-sm:mx-auto max-sm:flex max-sm:items-center
            md:pe-4
          "
        >
          <motion.span
            className="
              relative flex w-fit items-end justify-end bg-linear-to-t from-primary to-foreground
              bg-clip-text text-transparent will-change-transform
            "
            initial={{ opacity: 0, x: '-100%' }}
            animate={{ opacity: 1, x: '0%' }}
            transition={revealTransition(secondLineDuration)}
          >
            <motion.div
              className="absolute inset-0 bg-linear-to-r from-primary/0 to-primary"
              initial={{ opacity: 1 }}
              animate={{ opacity: 0 }}
              transition={revealTransition(2)}
            />
            {secondLine}
          </motion.span>
        </span>
      </Heading>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={descriptionTransition}
        className="
          text-xl/6.5 font-[450] tracking-tight text-pretty text-foreground
          max-sm:text-center
          sm:text-2xl/8
          md:text-3xl/9 md:font-medium
          lg:max-w-[24ch] lg:text-4xl/10
        "
      >
        {description}
      </motion.p>
    </div>
  );
};
